import React, { useState } from 'react';
import Select from '@atlaskit/select';
import { Admin, BillingAdminsPreferences } from './billing-and-licensing-emails';
import { NotificationLevel, NotificationPreferenceTypes } from './use-billing-admins-preference-handler';

interface AvailableOptionProps {
  label: string;
  value: {
    isEnabled: boolean;
    level: string | null;
  };
}

type UpdateAdminsPreferences = (
  aaid: string,
  type: string,
  hasEnabled: boolean,
  shouldManageNotificationAtBillingProfileLevel?: boolean,
  level?: string | null
) => Promise<{ success: boolean; response?: any; error?: any }>;

const availableOptions = [
  {
    label: 'No emails',
    value: {
      isEnabled: false,
      level: null,
    },
  },
  {
    label: 'Only my billing profiles',
    value: {
      isEnabled: true,
      level: NotificationLevel?.INVOICE_GROUP,
    },
  },
  {
    label: 'All emails',
    value: {
      isEnabled: true,
      level: null,
    },
  },
];

const getLabelAndValue = (emailPreference: { isEnabled: boolean | undefined; level: string | null | undefined }) => {
  if (emailPreference.isEnabled === false && !emailPreference?.level) {
    return availableOptions[0];
  } else if (emailPreference.isEnabled && emailPreference.level === NotificationLevel?.INVOICE_GROUP) {
    return availableOptions[1];
  } else {
    return availableOptions[2];
  }
};

export const PreferencesCell = ({
  billingAdmin,
  setPreferencesLoading,
  billingAdminsPreferences,
  setBillingAdminsPreferences,
  selectedAdmin,
  isDisabled,
  updateAdminsPreferences,
}: {
  billingAdmin: BillingAdminsPreferences | undefined;
  billingAdminsPreferences: BillingAdminsPreferences[];
  setPreferencesLoading: (value: boolean) => void;
  setBillingAdminsPreferences: (value: BillingAdminsPreferences[]) => void;
  selectedAdmin: Admin;
  isDisabled: boolean;
  updateAdminsPreferences: UpdateAdminsPreferences;
}) => {
  const [defaultValue, setDefaultValue] = useState(
    getLabelAndValue({ isEnabled: billingAdmin?.isEnabled, level: billingAdmin?.level })
  );

  const handleChange = async (admin: Admin, selectedOption: AvailableOptionProps) => {
    setPreferencesLoading(true);
    const previousPreferences = [...billingAdminsPreferences];
    const result = await updateAdminsPreferences(
      admin?.atlassianAccountId,
      NotificationPreferenceTypes.EMAIL,
      selectedOption?.value?.isEnabled,
      true,
      selectedOption?.value?.level
    );

    const updatedPreferences = billingAdminsPreferences?.map((billingAdmin: any) => {
      if (billingAdmin.atlassianAccountId === admin.atlassianAccountId) {
        // Replace the preference of the admin with the selected preferences
        return {
          ...billingAdmin,
          isEnabled: selectedOption?.value?.isEnabled,
          level: selectedOption?.value?.level,
        };
      } else {
        // Return the admin without any changes
        return billingAdmin;
      }
    });

    if (result.success) {
      setBillingAdminsPreferences(updatedPreferences as BillingAdminsPreferences[]);
      setDefaultValue(getLabelAndValue(selectedOption?.value));
    } else {
      setDefaultValue(getLabelAndValue({ isEnabled: billingAdmin?.isEnabled, level: billingAdmin?.level }));
      setBillingAdminsPreferences(previousPreferences);
    }
    setPreferencesLoading(false);
  };

  return (
    <div data-testid="select-preferences">
      <Select
        className="single-select"
        classNamePrefix="react-select"
        options={availableOptions}
        value={defaultValue}
        menuPortalTarget={document.body}
        onChange={(selectedOption: any) => handleChange(selectedAdmin, selectedOption)}
        isDisabled={isDisabled}
        appearance="default"
        data-testid={`select-preferences-${selectedAdmin.email}`}
      />
    </div>
  );
};
